import React, { useContext, useState } from 'react';
import { firestore } from './firebase';
import { updateDoc, doc } from 'firebase/firestore';
import { AppContext } from './AppContext';
import SPNListPageMediaItems from './SPNListPageMediaItems';
import { getAuth } from "firebase/auth";
import addToSPNLISTbutton from './assets/AddToSPNLISTbutton.png';
import { useLocation, useNavigate } from 'react-router-dom';

const SPNListPage = () => {
  const { playlistsFresh, setPlaylistsFresh, assetsSummary, library, userWallet } = useContext(AppContext);
  const [selectedMedia, setSelectedMedia] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { playlistId } = location.state;

  const getCurrentUserId = () => {
    const auth = getAuth();
    const user = auth.currentUser;
    return user ? user.uid : null;
  };

  const persistPlaylists = async (playlists) => {
    const userId = getCurrentUserId();
    try {
      await updateDoc(doc(firestore, "users", userId), { playlists });
    } catch (error) {
      console.error("Error updating user's playlists:", error);
    }
  };

  const addSongToPlaylist = (playlistId, songIdsToAdd) => {
    const songsToAdd = Array.isArray(songIdsToAdd) ? songIdsToAdd : [songIdsToAdd];

    const updatedPlaylists = (playlistsFresh || []).map(pl => {
      if (pl.id !== playlistId) return pl;

      const updatedSongs = Array.isArray(pl.songs) ? [...pl.songs] : [];
      let nextTrack =
        updatedSongs.reduce((m, s) => Math.max(m, Number(s?.trackNumber) || 0), 0) + 1;

      songsToAdd.forEach(id => {
        if (!updatedSongs.find(s => s?.contentId === id)) {
          updatedSongs.push({ contentId: id, trackNumber: nextTrack++ });
        }
      });

      return { ...pl, songs: updatedSongs };
    });

    setPlaylistsFresh(updatedPlaylists);
    persistPlaylists(updatedPlaylists);
  };

  const uniqByContentId = (arr=[]) => {
    const seen = new Set();
    return arr.filter(it => {
      const id = it?.contentId ?? it?.id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const extractSongsFromAlbums = (albums=[]) => {
    const out = [];
    albums.forEach(album => {
      if (!album?.songs || !Array.isArray(album.songs)) return;
      const albumKey = album.contentId ?? album.id;
      album.songs.forEach(song => {
        out.push({ ...song, albumId: albumKey, contentId: song.contentId ?? song.id });
      });
    });
    return out;
  };

  // Build selectable media from library + owned DATs
  const librarySingles = (library || [])
    .filter(x => x?.isNFT !== true && !x?.isAlbum)
    .map(x => ({ ...x, contentId: x.contentId ?? x.id }));

  const libraryAlbumTracks = extractSongsFromAlbums(
    (library || []).filter(x => x?.isNFT !== true && x?.isAlbum)
  );

  let nftFromOwned = [];
  if (assetsSummary && userWallet?.ownedDats?.length) {
    nftFromOwned = assetsSummary
      .filter(a => userWallet.ownedDats.some(od => od.id === a.id))
      .map(dat => ({ ...dat, contentId: dat.id, isNFT: true }));
  }

  const nftFromLibrary = (library || [])
    .filter(x => x?.isNFT === true)
    .map(libItem => {
      const cid = libItem.contentId ?? libItem.id;
      const matched = assetsSummary?.find(a => a.id === cid);
      return matched ? { ...matched, contentId: matched.id, isNFT: true }
                     : { ...libItem, contentId: cid, isNFT: true };
    });

  const datMedia = uniqByContentId([...nftFromOwned, ...nftFromLibrary]);

  const allMedia = uniqByContentId([
    ...librarySingles,
    ...libraryAlbumTracks,
    ...datMedia,
  ]);

  console.debug('[SPNListPage] allMedia sample', allMedia.slice(0,5).map(x => ({
    id: x.contentId || x.id,
    ArtworkURL: x.ArtworkURL || x.artworkURL || x.localArtworkURL,
    ArtistName: x.ArtistName || x.artistName,
    SongName: x.SongName || x.songName || x.title || x.name,
  })));

  const handleSelectMedia = (mediaId) => {
    setSelectedMedia(prev => prev.includes(mediaId) ? prev.filter(id => id !== mediaId) : [...prev, mediaId]);
  };

  const handleSubmit = () => {
    if (selectedMedia.length) addSongToPlaylist(playlistId, selectedMedia);
    navigate(`/library/spnlistprofile`, { state: { playlistId } });
  };

  return (
    <div style={styles.fullScreen}>
      <div style={styles.container}>
        <div style={{...styles.scrollContainer, zIndex: 10000, position:'relative'}}>
          <div style={styles.contentContainer}>
            <div style={styles.listHeader}>
              <button style={styles.createIconTouchable} onClick={handleSubmit}>
                <img src={addToSPNLISTbutton} style={styles.createIcon} alt="Add to SPNList" />
              </button>
            </div>

            {/* Force above any overlay; also resets any hostile CSS */}
            <div className="spn-visibility-reset" style={{ position:'relative', zIndex: 1000000 }}>
              {allMedia.map(item => (
                <SPNListPageMediaItems
                  key={item.contentId || item.id}
                  item={item}
                  onPress={() => handleSelectMedia(item.contentId)}
                  isSelected={selectedMedia.includes(item.contentId)}
                />
              ))}
            </div>

            <style>{`
              .spn-visibility-reset, .spn-visibility-reset * {
                opacity: 1 !important;
                visibility: visible !important;
                mix-blend-mode: normal !important;
                filter: none !important;
                -webkit-text-fill-color: inherit !important;
              }
            `}</style>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: { zIndex: 10, height: '82%', alignSelf: 'center', justifyContent: 'center', display: 'flex' },
  fullScreen: { position: 'relative', height: '100vh' },
  scrollContainer: { marginTop: '20px', position: 'absolute', top: 0, width: '100vw', height: '82vh', overflowY: 'auto' },
  contentContainer: { paddingBottom: '18vh', paddingLeft: '4.2vw', paddingRight: '4.2vw' },
  listHeader: { display: 'flex', justifyContent: 'center', marginBottom: '1vh' },
  createIcon: { height: '50px', width: '150px', alignSelf: 'center', zIndex: 100 },
  createIconTouchable: { marginTop: '10px', display:'flex', margin:'0 auto', justifyContent:'center', height:'5.2%', width:'22vw', alignSelf:'center', zIndex:100, background:'none', border:'none', cursor:'pointer' },
};

export default SPNListPage;
